/**
 * CountryFitBrowser — dedicated "Best Country to Launch" page.
 *
 * Lists every signal that has the country-fit analysis (recommended country +
 * an Agree/Disagree badge between the numbers-based score and the AI), and
 * opens the full side-by-side comparison inline when a row is clicked. Pro
 * gating has been removed, so everyone sees the complete analysis.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Globe, CheckCircle, Warning, MagnifyingGlass } from "@phosphor-icons/react";
import SideBySideOpportunityView from "./SideBySideOpportunityView";
import GuideHelpButton from "../components/GuideHelpButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_NAMES = {
  US: "United States", GB: "United Kingdom", IN: "India", CA: "Canada", AU: "Australia",
  DE: "Germany", FR: "France", ES: "Spain", IT: "Italy", NL: "Netherlands", SG: "Singapore",
  IE: "Ireland", SE: "Sweden", BR: "Brazil", JP: "Japan", AE: "United Arab Emirates",
  CH: "Switzerland", AT: "Austria", BE: "Belgium", DK: "Denmark", EE: "Estonia",
  FI: "Finland", IL: "Israel", KR: "South Korea", MX: "Mexico", NO: "Norway",
  NZ: "New Zealand", PL: "Poland", PT: "Portugal", SA: "Saudi Arabia", CZ: "Czech Republic",
};
const cname = (c) => COUNTRY_NAMES[c] || c || "—";

export default function CountryFitBrowser() {
  const [items, setItems] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => {
    axios.get(`${API}/arbitrage/available-opps`).then((r) => setItems(r.data.items || [])).catch(() => setItems([])).finally(() => setLoading(false));
    axios.get(`${API}/arbitrage/coverage`).then((r) => setCoverage(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Dedupe by problem text (the corpus contains near-duplicate signals) and
  // apply the search filter — keeps the discovery list clean and scannable.
  const seen = new Set();
  const filtered = items
    .filter((it) => !q.trim() || (it.problem || "").toLowerCase().includes(q.toLowerCase()) || cname(it.recommended_country).toLowerCase().includes(q.toLowerCase()))
    .filter((it) => {
      const key = (it.problem || `#${it.opportunity_id}`).trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  return (
    <div className="max-w-7xl mx-auto py-4 sm:py-6 px-2 sm:px-0" data-testid="country-fit-root">
      <div className="mb-5">
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-700 mb-1">Best country to launch</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 leading-tight flex items-center gap-2">
          <Globe size={28} weight="duotone" className="text-violet-600" /> Where should each idea launch?
          <GuideHelpButton feature="country" />
        </h1>
        <p className="text-sm text-zinc-600 mt-1 max-w-3xl leading-relaxed">
          For each opportunity, two independent methods — a numbers-based score and an AI — pick the best
          country to launch in. When they <strong>agree</strong>, it's a safe bet. When they
          <strong> disagree</strong>, we flag it so you can use your own judgment. Click any signal to see the full comparison.
        </p>
      </div>

      {/* Coverage progress */}
      {coverage && (
        <div className="mb-5 rounded-xl border border-zinc-200 bg-white p-4" data-testid="country-fit-coverage">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-semibold text-zinc-800">{coverage.analyzed} of {coverage.total} signals analyzed</span>
            <span className="text-zinc-500">{coverage.pct}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-zinc-100 overflow-hidden">
            <div className="h-full bg-violet-500 transition-all" style={{ width: `${Math.max(2, coverage.pct)}%` }} />
          </div>
          {coverage.remaining > 0 && (
            <p className="text-[12px] text-zinc-500 mt-2">
              We're analyzing the remaining {coverage.remaining} automatically in the background — and every new signal the scanner finds gets this too. Check back soon for more.
            </p>
          )}
        </div>
      )}

      {/* Inline side-by-side panel */}
      {openId && (
        <div className="mb-5 rounded-xl border border-violet-200 bg-white p-4" data-testid="country-fit-panel">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-bold text-zinc-800">Full comparison</span>
            <button onClick={() => setOpenId(null)} className="text-sm text-zinc-500 hover:text-zinc-900" data-testid="country-fit-close">✕ Close</button>
          </div>
          <SideBySideOpportunityView opportunity={{ id: openId }} onUpgradeClick={() => {}} />
        </div>
      )}

      <div className="mb-3 relative max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search signals or country…"
          className="w-full text-sm pl-9 pr-3 py-2 rounded-lg border border-zinc-200 focus:border-violet-500 focus:outline-none"
          data-testid="country-fit-search"
        />
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-10 text-center" data-testid="country-fit-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-zinc-500 py-10 text-center" data-testid="country-fit-empty">
          {items.length === 0 ? "No analyzed signals yet — the background job is generating them now. Check back in a few minutes." : "No signals match your search."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="country-fit-list">
          {filtered.map((it) => (
            <button
              key={it.opportunity_id}
              onClick={() => { setOpenId(it.opportunity_id); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className={`w-full text-left bg-white hover:bg-violet-50 transition-colors rounded-lg border px-4 py-3 ${openId === it.opportunity_id ? "border-violet-400 ring-1 ring-violet-300" : "border-zinc-200 hover:border-violet-300"}`}
              data-testid={`country-fit-row-${it.opportunity_id}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-[15px] font-semibold text-zinc-900 leading-snug line-clamp-2">{it.problem || `Opportunity #${it.opportunity_id}`}</h3>
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[12px]">
                    <span className="inline-flex items-center gap-1 font-medium text-zinc-700">
                      <Globe size={14} weight="duotone" className="text-violet-500" /> Best fit: <strong>{cname(it.recommended_country)}</strong>
                    </span>
                    {it.agree ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`country-fit-badge-${it.opportunity_id}`}>
                        <CheckCircle size={13} weight="fill" /> Both methods agree
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`country-fit-badge-${it.opportunity_id}`}>
                        <Warning size={13} weight="fill" /> Methods disagree — your call
                      </span>
                    )}
                    {it.has_alternative && it.alternative_country && (
                      <span className="text-zinc-500">AI also likes {cname(it.alternative_country)}</span>
                    )}
                  </div>
                </div>
                <span className="text-[11px] text-zinc-400 flex-shrink-0">View →</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
